import React, { useState, useEffect, useContext } from 'react';
import { Table, Button, Modal, Form, Spinner, Alert, Dropdown, ButtonGroup } from 'react-bootstrap';
import { FaEdit, FaTrash, FaBan, FaCheck, FaCopy } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import api from '../services/api';
import { trackAction } from '../services/analytics';
import usePermissions, { PERMISSIONS, ROLES } from '../hooks/usePermissions';
import toast from 'react-hot-toast';
import AuthContext from '../context/AuthContext';
import RolePermissionsModal from '../components/RolePermissionsModal';
import useIsMobile from '../hooks/useIsMobile';

const UsersPage = () => {
    const { user } = useContext(AuthContext);
    const { hasPermission } = usePermissions(user);
    const { t } = useTranslation();
    const isMobile = useIsMobile();
    const [users, setUsers] = useState([]);
    const [invitations, setInvitations] = useState([]);
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedUser, setSelectedUser] = useState(null);
    const [inviteForm, setInviteForm] = useState({ name: '', email: '', role: '' });
    const [generatedLink, setGeneratedLink] = useState(null);
    const [planLimit, setPlanLimit] = useState(null);
    const [showPermissionsModal, setShowPermissionsModal] = useState(false);
    const [selectedRole, setSelectedRole] = useState(null);
    const [selectedRoleName, setSelectedRoleName] = useState('');
    const [showEditInvitationModal, setShowEditInvitationModal] = useState(false);
    const [selectedInvitation, setSelectedInvitation] = useState(null);

    useEffect(() => {
        const fetchUsersAndInvites = async () => {
            setIsLoading(true);
            try {
                const [usersResponse, invitesResponse, limitResponse] = await Promise.all([
                    api.get('/users'),
                    api.get('/users/invitations'),
                    api.get('/users/limit')
                ]);
                setUsers(usersResponse.data);
                setInvitations(invitesResponse.data);
                setPlanLimit(limitResponse.data);
            } catch (err) {
                const errorMsg = t('usersPage.toasts.fetchFailed');
                setError(errorMsg);
                toast.error(errorMsg);
            } finally {
                setIsLoading(false);
            }
        };

        if (user && hasPermission(PERMISSIONS.MANAGE_USERS)) {
            fetchUsersAndInvites();
        } else {
            setIsLoading(false);
            setError(t('usersPage.toasts.permissionError'));
        }
    }, [user, hasPermission, t]);

    const handleInviteChange = (e) => {
        setInviteForm({ ...inviteForm, [e.target.name]: e.target.value });
    };

    const handleInviteSubmit = async (e) => {
        e.preventDefault();
        try {
            const response = await api.post('/users/invite', inviteForm);
            trackAction('user_invited', { role: inviteForm.role }); // sin PII: nada de email/nombre
            setUsers([...users, { ...response.data, type: 'invitation' }]);
            setGeneratedLink(response.data.invitation_link);

            // Show warning if limit reached
            if (response.data.limitWarning) {
                toast.warning(response.data.limitWarning.message, { duration: 6000 });
            }

            // Refresh limit info
            const limitResponse = await api.get('/users/limit');
            setPlanLimit(limitResponse.data);
        } catch (err) {
            const errorMessage = err.response?.data?.message || t('usersPage.toasts.inviteFailed');
            trackAction('user_invite_failed');
            setError(errorMessage);
            toast.error(errorMessage);
        }
    };

    const handleCloseInviteModal = () => {
        setShowInviteModal(false);
        setInviteForm({ name: '', email: '', role: '' });
        setGeneratedLink(null);
        setError(null);
    };

    const copyToClipboard = (link) => {
        navigator.clipboard.writeText(link).then(() => {
            toast.success(t('usersPage.toasts.copySuccess'));
        }, (err) => {
            toast.error(t('usersPage.toasts.copyFailed'));
        });
    };

    const handleEditClick = (user) => {
        setSelectedUser(user);
        setShowEditModal(true);
    };


    const handleEditChange = (e) => {
        setSelectedUser({ ...selectedUser, [e.target.name]: e.target.value });
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();
        try {
            const response = await api.put(`/users/${selectedUser.id}`, {
                role: selectedUser.role,
                status: selectedUser.status,
            });
            trackAction('user_updated', { role: selectedUser.role, status: selectedUser.status });
            setUsers(users.map(u => u.id === selectedUser.id ? { ...response.data, type: 'user' } : u));
            setShowEditModal(false);
            setSelectedUser(null);

            // Refresh limit info
            const limitResponse = await api.get('/users/limit');
            setPlanLimit(limitResponse.data);

            toast.success(t('usersPage.toasts.updateSuccess') || 'Usuario actualizado correctamente');
        } catch (err) {
            const errorMessage = err.response?.data?.message || t('usersPage.toasts.updateFailed');

            // Check if it's a limit error
            if (err.response?.status === 403 && err.response?.data?.code === 'USER_LIMIT_REACHED') {
                toast.error(t('usersPage.toasts.limitExceeded'));
            } else {
                toast.error(errorMessage);
            }
            setError(errorMessage);
        }
    };

    const handleDelete = async (userId) => {
        const result = await Swal.fire({
            title: t('areYouSure'),
            text: t('usersPage.toasts.deactivateConfirm'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: 'var(--ev-action-danger-bg)',
            cancelButtonColor: 'var(--ev-bg-subtle)',
            confirmButtonText: t('yesDelete'),
            cancelButtonText: t('cancel')
        });
        if (result.isConfirmed) {
            try {
                const response = await api.delete(`/users/${userId}`);
                trackAction('user_deactivated'); // DELETE = baja/desactivación (soft delete)
                setUsers(users.map(u => u.id === userId ? { ...response.data, type: 'user' } : u));

                // Refresh limit info
                const limitResponse = await api.get('/users/limit');
                setPlanLimit(limitResponse.data);
            } catch (err) {
                setError(err.response?.data?.message || t('usersPage.toasts.deactivateFailed'));
            }
        }
    };

    const handleDeleteInvitation = async (invitationId) => {
        const result = await Swal.fire({
            title: t('areYouSure'),
            text: t('usersPage.toasts.deleteInviteConfirm'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: 'var(--ev-action-danger-bg)',
            cancelButtonColor: 'var(--ev-bg-subtle)',
            confirmButtonText: t('yesDelete'),
            cancelButtonText: t('cancel')
        });
        if (result.isConfirmed) {
            try {
                await api.delete(`/users/invitations/${invitationId}`);
                trackAction('invitation_deleted');
                setUsers(users.filter(u => u.id !== invitationId));
            } catch (err) {
                setError(err.response?.data?.message || t('usersPage.toasts.deleteInviteFailed'));
            }
        }
    };

    const canManage = hasPermission(PERMISSIONS.MANAGE_USERS);
    const canDelete = hasPermission(PERMISSIONS.DELETE_USERS);
    const isOwner = user?.role === ROLES.OWNER || user?.role === ROLES.SUPERADMIN;

    const handleEditInvitationClick = (invitation) => {
        setSelectedInvitation({ ...invitation });
        setShowEditInvitationModal(true);
    };

    const handleEditInvitationSubmit = async (e) => {
        e.preventDefault();
        try {
            const response = await api.put(`/users/invitations/${selectedInvitation.id}`, {
                role: selectedInvitation.role,
            });
            trackAction('invitation_updated', { role: selectedInvitation.role });
            setUsers(users.map(u => u.id === selectedInvitation.id && u.type === 'invitation'
                ? { ...u, role: response.data.role }
                : u
            ));
            setShowEditInvitationModal(false);
            setSelectedInvitation(null);
            toast.success(t('usersPage.toasts.updateSuccess') || 'Invitación actualizada');
        } catch (err) {
            toast.error(err.response?.data?.message || t('usersPage.toasts.updateFailed'));
        }
    };

    const handleConfigurePermissions = (role, roleName) => {
        setSelectedRole(role);
        setSelectedRoleName(roleName);
        setShowPermissionsModal(true);
    };

    const handlePermissionsModalClose = (changed) => {
        setShowPermissionsModal(false);
        setSelectedRole(null);
        setSelectedRoleName('');
        // Optionally refresh data if permissions were changed
    };

    if (isLoading) {
        return <div className="d-flex justify-content-center align-items-center" style={{ height: '80vh' }}><Spinner animation="border" /></div>;
    }

    if (!canManage) {
        return (
            <div>
                <h1>{t('usersPage.title')}</h1>
                <Alert variant="danger">{t('usersPage.toasts.permissionError')}</Alert>
            </div>
        );
    }

    return (
        <div>
            <h1>{t('usersPage.title')}</h1>
            {error && <Alert variant="danger">{error}</Alert>}

            {/* Plan Limit Info */}
            {planLimit && (
                <Alert variant={planLimit.canAdd ? 'info' : 'warning'} className="mb-3">
                    <strong>{t('usersPage.limitReached', { current: planLimit.current, limit: planLimit.limit === -1 ? '∞' : planLimit.limit })}</strong>
                    {!planLimit.canAdd && (
                        <div className="mt-1">
                            <small>{t('usersPage.upgradePlan')}</small>
                        </div>
                    )}
                </Alert>
            )}

            {canManage && (
                <div className="d-flex gap-2 mb-3">
                    <Button
                        variant="primary"
                        onClick={() => setShowInviteModal(true)}
                        disabled={planLimit && !planLimit.canAdd}
                        title={planLimit && !planLimit.canAdd ? t('usersPage.cannotActivate') : ''}
                    >
                        {t('usersPage.inviteUserButton')}
                    </Button>

                    {isOwner && (
                        <Dropdown>
                            <Dropdown.Toggle variant="outline-secondary" id="roles-dropdown">
                                {t('usersPage.rolePermissions.rolesDropdown')}
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                                <div className="px-3 py-2" style={{ minWidth: '200px' }}>
                                    <div className="d-flex justify-content-between align-items-center py-2">
                                        <span className="me-2 me-sm-4"><strong>{t('usersPage.roles.admin')}</strong></span>
                                        <Button
                                            variant="link"
                                            size="sm"
                                            className="p-0 text-primary text-nowrap"
                                            onClick={() => handleConfigurePermissions('admin', t('usersPage.roles.admin'))}
                                        >
                                            <span className="d-none d-sm-inline">{t('usersPage.rolePermissions.modifyPermissions')}</span>
                                            <i className="bi bi-pencil-square d-sm-none"></i>
                                        </Button>
                                    </div>
                                    <hr className="my-1" />
                                    <div className="d-flex justify-content-between align-items-center py-2">
                                        <span className="me-2 me-sm-4"><strong>{t('usersPage.roles.employee')}</strong></span>
                                        <Button
                                            variant="link"
                                            size="sm"
                                            className="p-0 text-primary text-nowrap"
                                            onClick={() => handleConfigurePermissions('employee', t('usersPage.roles.employee'))}
                                        >
                                            <span className="d-none d-sm-inline">{t('usersPage.rolePermissions.modifyPermissions')}</span>
                                            <i className="bi bi-pencil-square d-sm-none"></i>
                                        </Button>
                                    </div>
                                </div>
                            </Dropdown.Menu>
                        </Dropdown>
                    )}
                </div>
            )}

            <div className="modern-table-wrapper">
                <Table hover responsive className="modern-table">
                    <thead>
                        <tr>
                            <th>{t('usersPage.table.name')}</th>
                            <th>{t('usersPage.table.email')}</th>
                            <th>{t('usersPage.table.role')}</th>
                            <th>{t('usersPage.table.status')}</th>
                            {canManage && <th>{t('usersPage.table.actions')}</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {users.map(user => (
                            <tr key={`${user.type}-${user.id}`}>
                                <td>{user.name || '-'}</td>
                                <td>{user.email}</td>
                                <td>{t(`usersPage.roles.${user.role}`)}</td>
                                <td>
                                    <span className={`badge bg-${user.status === 'active' ? 'success' : user.status === 'pending' ? 'warning' : 'secondary'}`}>
                                        {t(`usersPage.statuses.${user.status}`)}
                                    </span>
                                </td>
                                {canManage && (
                                    <td className="text-center">
                                        <div className="btn-actions">
                                        {user.type === 'user' ? (
                                            <>
                                                <Button
                                                    variant="info"
                                                    size="sm"
                                                    onClick={() => handleEditClick(user)}
                                                    disabled={user.role === 'owner'}
                                                    title={t('usersPage.editButton')}
                                                    aria-label={t('usersPage.editButton')}
                                                >
                                                    <FaEdit />
                                                </Button>
                                                {user.status === 'active' ? (
                                                    <Button
                                                        variant="danger"
                                                        size="sm"
                                                        onClick={() => handleDelete(user.id)}
                                                        disabled={user.role === 'owner' || !canDelete}
                                                        title={t('usersPage.deactivateButton')}
                                                        aria-label={t('usersPage.deactivateButton')}
                                                    >
                                                        <FaBan />
                                                    </Button>
                                                ) : (
                                                    <Button
                                                        variant="success"
                                                        size="sm"
                                                        onClick={async () => {
                                                            try {
                                                                const response = await api.put(`/users/${user.id}`, {
                                                                    role: user.role,
                                                                    status: 'active',
                                                                });
                                                                setUsers(users.map(u => u.id === user.id ? { ...response.data, type: 'user' } : u));

                                                                // Refresh limit info
                                                                const limitResponse = await api.get('/users/limit');
                                                                setPlanLimit(limitResponse.data);

                                                                toast.success(t('usersPage.toasts.updateSuccess') || 'Usuario activado correctamente');
                                                            } catch (err) {
                                                                const errorMessage = err.response?.data?.message || t('usersPage.toasts.updateFailed');

                                                                // Check if it's a limit error
                                                                if (err.response?.status === 403 && err.response?.data?.code === 'USER_LIMIT_REACHED') {
                                                                    toast.error(t('usersPage.toasts.limitExceeded'));
                                                                } else {
                                                                    toast.error(errorMessage);
                                                                }
                                                            }
                                                        }}
                                                        disabled={planLimit && !planLimit.canAdd}
                                                        title={planLimit && !planLimit.canAdd ? t('usersPage.cannotActivate') : t('usersPage.activateButton')}
                                                        aria-label={t('usersPage.activateButton')}
                                                    >
                                                        <FaCheck />
                                                    </Button>
                                                )}
                                            </>
                                        ) : (
                                            <>
                                                <Button
                                                    variant="info"
                                                    size="sm"
                                                    onClick={() => handleEditInvitationClick(user)}
                                                    title={t('usersPage.editButton')}
                                                    aria-label={t('usersPage.editButton')}
                                                >
                                                    <FaEdit />
                                                </Button>
                                                <Button
                                                    variant="secondary"
                                                    size="sm"
                                                    onClick={() => copyToClipboard(user.invitation_link)}
                                                    title={t('usersPage.copyLinkButton')}
                                                    aria-label={t('usersPage.copyLinkButton')}
                                                >
                                                    <FaCopy />
                                                </Button>
                                                <Button
                                                    variant="danger"
                                                    size="sm"
                                                    onClick={() => handleDeleteInvitation(user.id)}
                                                    title={t('usersPage.deleteInvitationButton')}
                                                    aria-label={t('usersPage.deleteInvitationButton')}
                                                >
                                                    <FaTrash />
                                                </Button>
                                            </>
                                        )}
                                        </div>
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </div>

            {/* Invite User Modal */}
            <Modal show={showInviteModal} onHide={handleCloseInviteModal} backdrop={isMobile ? 'static' : true}>
                <Modal.Header closeButton>
                    <Modal.Title>{t('usersPage.inviteModal.title')}</Modal.Title>
                </Modal.Header>
                {generatedLink ? (
                    <Modal.Body>
                        <Alert variant="success">{t('usersPage.inviteModal.success')}</Alert>
                        <p>{t('usersPage.inviteModal.shareMessage')}</p>
                        <Form.Control
                            type="text"
                            value={generatedLink}
                            readOnly
                            className="mb-2"
                        />
                        <Button variant="secondary" onClick={() => copyToClipboard(generatedLink)}>
                            {t('usersPage.inviteModal.copyButton')}
                        </Button>
                    </Modal.Body>
                ) : (
                    <Form onSubmit={handleInviteSubmit}>
                        <Modal.Body>
                            <Form.Group className="mb-3">
                                <Form.Label>{t('usersPage.inviteModal.nameLabel')}</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="name"
                                    placeholder={t('usersPage.inviteModal.namePlaceholder')}
                                    value={inviteForm.name}
                                    onChange={handleInviteChange}
                                    required
                                />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>{t('usersPage.inviteModal.emailLabel')}</Form.Label>
                                <Form.Control
                                    type="email"
                                    name="email"
                                    value={inviteForm.email}
                                    onChange={handleInviteChange}
                                    required
                                />
                            </Form.Group>
                            <Form.Group>
                                <Form.Label>{t('usersPage.inviteModal.roleLabel')}</Form.Label>
                                <Form.Select name="role" value={inviteForm.role} onChange={handleInviteChange} required>
                                    <option value="" disabled>{t('usersPage.selectRole')}</option>
                                    <option value="admin">{t('usersPage.roles.admin')}</option>
                                    <option value="employee">{t('usersPage.roles.employee')}</option>
                                </Form.Select>
                            </Form.Group>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={handleCloseInviteModal}>
                                {t('usersPage.inviteModal.closeButton')}
                            </Button>
                            <Button variant="primary" type="submit">
                                {t('usersPage.inviteModal.sendButton')}
                            </Button>
                        </Modal.Footer>
                    </Form>
                )}
            </Modal>

            {/* Edit User Modal */}
            {selectedUser && (
                <Modal show={showEditModal} onHide={() => setShowEditModal(false)} backdrop={isMobile ? 'static' : true}>
                    <Modal.Header closeButton>
                        <Modal.Title>{t('usersPage.editModal.title', { email: selectedUser.email })}</Modal.Title>
                    </Modal.Header>
                    <Form onSubmit={handleEditSubmit}>
                        <Modal.Body>
                            <Form.Group className="mb-3">
                                <Form.Label>{t('usersPage.editModal.roleLabel')}</Form.Label>
                                <Form.Select name="role" value={selectedUser.role} onChange={handleEditChange}>
                                    <option value="admin">{t('usersPage.roles.admin')}</option>
                                    <option value="employee">{t('usersPage.roles.employee')}</option>
                                </Form.Select>
                            </Form.Group>
                            <Form.Group>
                                <Form.Label>{t('usersPage.editModal.statusLabel')}</Form.Label>
                                <Form.Select
                                    name="status"
                                    value={selectedUser.status}
                                    onChange={handleEditChange}
                                >
                                    <option
                                        value="active"
                                        disabled={selectedUser.status === 'inactive' && planLimit && !planLimit.canAdd}
                                    >
                                        {t('usersPage.statuses.active')}
                                        {selectedUser.status === 'inactive' && planLimit && !planLimit.canAdd && ' (Límite alcanzado)'}
                                    </option>
                                    <option value="inactive">{t('usersPage.statuses.inactive')}</option>
                                </Form.Select>
                                {selectedUser.status === 'inactive' && planLimit && !planLimit.canAdd && (
                                    <Form.Text className="text-warning">
                                        {t('usersPage.cannotActivate')}
                                    </Form.Text>
                                )}
                            </Form.Group>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={() => setShowEditModal(false)}>
                                {t('usersPage.editModal.closeButton')}
                            </Button>
                            <Button variant="primary" type="submit">
                                {t('usersPage.editModal.saveButton')}
                            </Button>
                        </Modal.Footer>
                    </Form>
                </Modal>
            )}


            {/* Edit Invitation Modal */}
            {selectedInvitation && (
                <Modal show={showEditInvitationModal} onHide={() => setShowEditInvitationModal(false)} backdrop={isMobile ? 'static' : true}>
                    <Modal.Header closeButton>
                        <Modal.Title>{t('usersPage.editModal.title', { email: selectedInvitation.email })}</Modal.Title>
                    </Modal.Header>
                    <Form onSubmit={handleEditInvitationSubmit}>
                        <Modal.Body>
                            <Form.Group>
                                <Form.Label>{t('usersPage.editModal.roleLabel')}</Form.Label>
                                <Form.Select
                                    value={selectedInvitation.role}
                                    onChange={(e) => setSelectedInvitation({ ...selectedInvitation, role: e.target.value })}
                                >
                                    <option value="admin">{t('usersPage.roles.admin')}</option>
                                    <option value="employee">{t('usersPage.roles.employee')}</option>
                                </Form.Select>
                            </Form.Group>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={() => setShowEditInvitationModal(false)}>
                                {t('usersPage.editModal.closeButton')}
                            </Button>
                            <Button variant="primary" type="submit">
                                {t('usersPage.editModal.saveButton')}
                            </Button>
                        </Modal.Footer>
                    </Form>
                </Modal>
            )}

            {/* Role Permissions Modal */}
            <RolePermissionsModal
                show={showPermissionsModal}
                onHide={handlePermissionsModalClose}
                role={selectedRole}
                roleName={selectedRoleName}
            />
        </div>
    );
};

export default UsersPage;
